import { format } from 'date-fns';

function formatReadDate(raw) {
    if (!raw) return '—';
    try {
        return format(new Date(raw), 'd MMM yyyy');
    } catch {
        return '—';
    }
}

function accessGroupLabel(raw) {
    if (!raw) return '—';
    const text = String(raw).trim();
    const colon = text.indexOf(':');
    if (colon >= 0) {
        const name = text.slice(colon + 1).trim();
        return name || text;
    }
    return text;
}

export function readersFromFile(file, projectUsersAll = []) {
    const ids = Array.isArray(file?.read_by) ? file.read_by : [];
    const readDateByMember = new Map();
    (file?.read_by_details || []).forEach(row => {
        if (row?.group_member_id != null) {
            readDateByMember.set(String(row.group_member_id), row.read_date);
        }
    });
    const fallbackDate = file?.read_date || null;
    return ids.map(id => {
        const row = projectUsersAll.find(item => String(item?.value?.group_member_id) === String(id));
        const perReaderDate = readDateByMember.get(String(id));
        const lastRead = formatReadDate(perReaderDate || (readDateByMember.size ? null : fallbackDate));
        return {
            id,
            name: row?.value?.full_name || 'Unknown',
            accessGroup: accessGroupLabel(row?.group),
            lastRead,
        };
    });
}

export function documentIdOf(value) {
    if (value == null) return null;
    if (typeof value === 'object') return value.id ?? value.pk ?? null;
    return value;
}

export function irlStatusLabel(item) {
    if (item?.irresponse?.answer) return 'Answered';
    if (item?.status === true || item?.status === 'answered') return 'Answered';
    return 'In progress';
}

export function relatedForDocuments(documentIds, informationRequests = [], observations = []) {
    const ids = new Set((documentIds || []).filter(id => id != null).map(id => String(id)));
    const irl = (informationRequests || [])
        .filter(item => ids.has(String(documentIdOf(item?.referenced_file))))
        .map(item => ({
            id: item.id,
            label: item.prefix ? `${item.prefix}${item.item ?? ''}` : `IRL-${item.id}`,
            title: item.request || item.title || 'Information request',
            status: irlStatusLabel(item),
        }));
    const obs = (observations || [])
        .filter(item => ids.has(String(documentIdOf(item?.docitem))))
        .map(item => ({
            id: item.id,
            label: `OBS-${item.id}`,
            title: item.observation || 'Observation',
            description: item.comments || '',
        }));
    return { irl, obs };
}

export function observationsForRecording(recordingId, observations = []) {
    if (!recordingId) return [];
    return (observations || [])
        .filter(item => String(item?.meeting_recording) === String(recordingId))
        .map(item => ({
            id: item.id,
            label: `OBS-${item.id}`,
            title: item.observation || 'Observation',
            description: item.comments || '',
        }));
}

function textMatches(query, parts) {
    const needle = String(query || '').trim().toLowerCase();
    if (!needle) return false;
    return parts
        .filter(part => part != null && part !== '')
        .join(' ')
        .toLowerCase()
        .includes(needle);
}

export function mailSearchHits(query, mails = []) {
    return mails
        .filter(mail =>
            textMatches(query, [mail.subject, mail.senderName, mail.senderEmail, mail.body, ...(mail.attachments || []).map(item => item.name)]),
        )
        .map(mail => ({
            id: `mail-${mail.id}`,
            kind: 'mail',
            sourceId: mail.id,
            document_name: mail.subject || 'Forwarded email',
            path: 'Project Mail',
            snippet: String(mail.body || '').replace(/\s+/g, ' ').trim().slice(0, 180),
            status: mail.status,
        }));
}

const TEAMS_CUE_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(\/\d+-\d+)?$/i;
const WEBVTT_CUE_TIME = /^((?:\d{1,2}:)?\d{2}:\d{2}[.,]\d{1,3})\s*-->\s*(?:\d{1,2}:)?\d{2}:\d{2}/;
const WEBVTT_VOICE_TAG = /^<v\s+([^>]+)>([\s\S]*?)<\/v>$/i;

export function formatTranscriptSpeaker(name) {
    const trimmed = String(name || '').trim();
    if (!trimmed) return '';
    if (trimmed.startsWith('@')) {
        const suffix = trimmed.slice(1).trim();
        return suffix ? `Speaker ${suffix}` : 'Speaker';
    }
    return trimmed;
}

export function formatTranscriptTime(time) {
    const raw = String(time || '').trim();
    if (!raw) return '';
    const withoutMs = raw.replace(/[.,]\d{1,3}$/, '');
    const parts = withoutMs.split(':');
    if (parts.length === 2) {
        return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
    }
    if (parts.length === 3) {
        return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}:${parts[2].padStart(2, '0')}`;
    }
    return withoutMs;
}

export function formatTranscriptQuote(text) {
    const trimmed = String(text || '').trim();
    if (!trimmed) return '';
    if (trimmed.startsWith('"') && trimmed.endsWith('"')) return trimmed;
    return `"${trimmed}"`;
}

/** Repair transcripts saved before MS Teams WebVTT parsing was fixed. */
export function normalizeRecordingTranscript(transcript) {
    if (!Array.isArray(transcript) || transcript.length === 0) return [];
    const looksBroken = transcript.some(line => {
        const text = String(line?.text || '').trim();
        return WEBVTT_VOICE_TAG.test(text) || TEAMS_CUE_ID.test(text) || WEBVTT_CUE_TIME.test(text);
    });
    const polishLine = line => ({
        ...line,
        speaker: formatTranscriptSpeaker(line?.speaker),
    });

    if (!looksBroken) return (transcript || []).map(polishLine);

    const out = [];
    let pendingTime = '';
    transcript.forEach(line => {
        let text = String(line?.text || '').trim();
        if (!text || text.toUpperCase() === 'WEBVTT') return;
        if (TEAMS_CUE_ID.test(text)) return;

        const timeMatch = text.match(WEBVTT_CUE_TIME);
        if (timeMatch) {
            pendingTime = timeMatch[1].replace(',', '.');
            return;
        }

        let speaker = line?.speaker || '';
        let time = line?.time || pendingTime;
        const voiceMatch = text.match(WEBVTT_VOICE_TAG);
        if (voiceMatch) {
            speaker = speaker || formatTranscriptSpeaker(voiceMatch[1]);
            text = voiceMatch[2].trim();
        }
        pendingTime = '';
        if (text) {
            out.push({
                time,
                speaker: formatTranscriptSpeaker(speaker),
                text,
            });
        }
    });
    return (out.length ? out : transcript || []).map(polishLine);
}

export function recordingSearchHits(query, recordings = []) {
    return recordings
        .filter(recording =>
            textMatches(query, [
                recording.title,
                recording.source,
                ...(recording.participantNames || []),
                ...normalizeRecordingTranscript(recording.transcript || []).map(
                    line => `${line.speaker || ''} ${line.text || ''}`,
                ),
                ...(recording.aiSnapshot?.keyTopics || []),
                ...(recording.aiSnapshot?.keyInformation || []),
            ]),
        )
        .map(recording => ({
            id: `recording-${recording.id}`,
            kind: 'recording',
            sourceId: recording.id,
            document_name: recording.title || 'Meeting recording',
            path: 'Meeting Recordings',
            snippet: normalizeRecordingTranscript(recording.transcript || [])[0]?.text || '',
            status: recording.status,
        }));
}

export function stripHtml(value) {
    return String(value || '')
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}

function tryParseJson(value) {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return null;
    try {
        return JSON.parse(trimmed);
    } catch {
        return null;
    }
}

function normalizeSections(sections) {
    if (!sections) return {};
    if (typeof sections === 'string') {
        return tryParseJson(sections) || {};
    }
    return sections;
}

function pickSnapshotField(sections) {
    return (
        sections.quick_snapshot ||
        sections.quick_snapshot_html ||
        sections.executive_summary_html ||
        sections.executive_summary ||
        ''
    );
}

export function snapshotText(analysis) {
    if (!analysis || analysis.has_analysis === false) return '';
    const sections = normalizeSections(analysis.sections);
    let raw = pickSnapshotField(sections);
    if (typeof raw !== 'string') raw = String(raw || '');

    const embedded = tryParseJson(raw);
    if (embedded && typeof embedded === 'object') {
        raw = pickSnapshotField(embedded);
    }

    return stripHtml(raw);
}
